import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '@shared/services/api';
import TeamMembersContent from './TeamMembersContent';
import BillingContent from '@features/billing/pages/BillingContent';
import StorageSettingsContent from '@features/storage/pages/StorageSettingsContent';
import ImpersonationSensitiveBanner from '@shared/components/ImpersonationSensitiveBanner';
import ReadOnlyBadge from '@shared/components/ReadOnlyBadge';
import { Card, Button, Input } from '@shared/components/ui';
import { Settings as SettingsIcon, Users, CreditCard, Database, Info } from 'lucide-react';

const AUTO_DISMISS_DELAY = 5000;

interface SettingsData {
  max_concurrent_backups: number;
}

type SettingsTab = 'general' | 'team' | 'billing' | 'storage';
const SETTINGS_TABS: SettingsTab[] = ['general', 'team', 'billing', 'storage'];

function getSettingsTab(value: string | null): SettingsTab {
  return SETTINGS_TABS.includes(value as SettingsTab) ? (value as SettingsTab) : 'general';
}

export default function Settings() {
  const { t } = useTranslation('settings');
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<SettingsTab>(() =>
    getSettingsTab(searchParams.get('tab'))
  );
  const [settings, setSettings] = useState<SettingsData>({
    max_concurrent_backups: 5,
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, AUTO_DISMISS_DELAY);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setLoadError(false);
    setMessage(null);
    try {
      const response = await api.get<SettingsData>('/settings/max-concurrent-backups');
      setSettings(response.data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (
      loading ||
      loadError ||
      settings.max_concurrent_backups < 1 ||
      settings.max_concurrent_backups > 100
    ) {
      setMessage({ type: 'error', text: t('settings.errors.invalidRange') });
      return;
    }

    try {
      setSaving(true);
      await api.put('/settings/max-concurrent-backups', {
        max_concurrent_backups: settings.max_concurrent_backups,
      });
      setMessage({ type: 'success', text: t('settings.success.saved') });
    } catch {
      setMessage({
        type: 'error',
        text: t('settings.errors.saveFailed'),
      });
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'general', label: t('settings.tabs.general'), icon: <SettingsIcon size={18} /> },
    { id: 'team', label: t('settings.tabs.team'), icon: <Users size={18} /> },
    { id: 'billing', label: t('settings.tabs.billing'), icon: <CreditCard size={18} /> },
    { id: 'storage', label: t('settings.tabs.storage'), icon: <Database size={18} /> },
  ];

  return (
    <>
      <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-300">
        <ImpersonationSensitiveBanner />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight">
                {t('title')}
              </h1>
              <ReadOnlyBadge />
            </div>
            <p className="text-sm sm:text-base text-text-muted-light dark:text-text-muted-dark mt-1">
              {t('subtitle')}
            </p>
          </div>
        </div>

        {message && (
          <div
            role={message.type === 'error' ? 'alert' : 'status'}
            aria-live="polite"
            className={`p-4 rounded-xl border ${
              message.type === 'success'
                ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20 text-state-ok-light dark:text-state-ok-dark'
                : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20 text-state-danger-light dark:text-state-danger-dark'
            }`}
          >
            <p className="font-bold text-sm">{message.text}</p>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-5 sm:gap-6">
          {/* Vertical Tabs Sidebar */}
          <div className="w-full lg:w-64 shrink-0">
            <Card className="p-2 sticky top-6">
              <nav aria-label={t('settings.tabs.label')} className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    id={`settings-tab-${tab.id}`}
                    aria-label={tab.label}
                    aria-pressed={activeTab === tab.id}
                    aria-controls="settings-panel"
                    onClick={() => {
                      const selectedTab = tab.id as SettingsTab;
                      setActiveTab(selectedTab);
                      setSearchParams(
                        (current) => {
                          const next = new URLSearchParams(current);
                          if (selectedTab === 'general') {
                            next.delete('tab');
                          } else {
                            next.set('tab', selectedTab);
                          }
                          return next;
                        },
                        { replace: true }
                      );
                    }}
                    className={`w-full min-h-11 flex items-center px-4 py-3 text-sm font-semibold rounded-xl transition-all ${
                      activeTab === tab.id
                        ? 'bg-accent-soft-light dark:bg-accent-soft-dark text-accent-text-light dark:text-accent-text-dark'
                        : 'text-text-muted-light dark:text-text-muted-dark hover:bg-surface-2-light dark:hover:bg-surface-2-dark'
                    }`}
                  >
                    <span className="mr-3 opacity-70">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </Card>
          </div>

          {/* Content Area */}
          <div id="settings-panel" className="flex-1 min-w-0">
            {activeTab === 'general' && (
              <Card className="p-5 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold text-text-primary-light dark:text-text-primary-dark mb-5">
                  {t('general.configuration')}
                </h2>

                {loading && (
                  <p
                    role="status"
                    className="py-8 text-center text-sm text-text-muted-light dark:text-text-muted-dark"
                  >
                    {t('settings.general.loading')}
                  </p>
                )}
                {loadError && (
                  <div
                    role="alert"
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-state-danger-light/20 dark:border-state-danger-dark/20 bg-state-danger-bg-light dark:bg-state-danger-bg-dark p-4 text-sm font-medium text-state-danger-light dark:text-state-danger-dark"
                  >
                    <span>{t('settings.errors.loadFailed')}</span>
                    <Button variant="outline" onClick={fetchSettings}>
                      {t('settings.general.retry')}
                    </Button>
                  </div>
                )}
                {!loading && !loadError && (
                  <form onSubmit={handleSave} className="space-y-6 sm:space-y-8">
                    <div className="space-y-4">
                      <Input
                        id="max-concurrent-backups"
                        label={t('general.maxConcurrentBackups')}
                        type="number"
                        min="1"
                        max="100"
                        value={settings.max_concurrent_backups}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            max_concurrent_backups: parseInt(e.target.value) || 1,
                          })
                        }
                        aria-describedby="max-concurrent-backups-description"
                        className="max-w-xs"
                      />
                      <p
                        id="max-concurrent-backups-description"
                        className="text-xs text-text-muted-light dark:text-text-muted-dark font-medium"
                      >
                        {t('general.maxConcurrentBackupsDesc')}
                      </p>
                    </div>

                    <div className="bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 p-4 rounded-xl flex items-start gap-3">
                      <Info
                        className="text-state-info-light dark:text-state-info-dark shrink-0 mt-0.5"
                        size={20}
                      />
                      <div className="text-sm">
                        <p className="font-bold text-state-info-light dark:text-state-info-dark mb-1">
                          {t('general.queueManagement')}
                        </p>
                        <p className="text-state-info-light dark:text-state-info-dark leading-relaxed">
                          {t('general.queueManagementDesc', {
                            limit: settings.max_concurrent_backups,
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
                      <Button type="submit" isLoading={saving}>
                        {t('settings.general.saveChanges')}
                      </Button>
                    </div>
                  </form>
                )}
              </Card>
            )}

            {activeTab === 'team' && <TeamMembersContent />}
            {activeTab === 'billing' && <BillingContent />}
            {activeTab === 'storage' && <StorageSettingsContent />}
          </div>
        </div>
      </div>
    </>
  );
}
